import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowDown, ArrowLeft, ArrowUp, Copy as CopyIcon, Flag, Pencil,
  PenTool, Plus, Printer, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { useLang } from "@/context/LanguageContext";
import { CopyButton, EmptyState } from "@/components/shared";
import { RoomDialog, sectionsTotal, roomArea } from "@/components/RoomDialog";
import { RoomSketch } from "@/components/RoomSketch";

const round1 = (v) => Math.round(v * 10) / 10;

export default function Rooms() {
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [editRoom, setEditRoom] = useState(undefined); // undefined closed, null new, obj edit
  const [sketchRoom, setSketchRoom] = useState(null);
  const { t, lang } = useLang();

  const load = useCallback(() => {
    api.get(`/properties/${propertyId}`).then((r) => setProperty(r.data)).catch(() => {});
    api.get("/rooms", { params: { property_id: propertyId } }).then((r) => setRooms(r.data)).catch(() => {});
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const habitable = round1(rooms.filter((r) => !r.exclude_habitable).reduce((s, r) => s + roomArea(r), 0));

  const reorder = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rooms.length) return;
    await Promise.all([
      api.put(`/rooms/${rooms[i].id}`, { sort_order: j }),
      api.put(`/rooms/${rooms[j].id}`, { sort_order: i }),
    ]);
    load();
  };

  const duplicate = async (room) => {
    const { id, created_at, ...rest } = room;
    await api.post("/rooms", { ...rest, name: `${room.name} (copy)`, sort_order: rooms.length });
    toast.success(t("saved"));
    load();
  };

  const removeRoom = async (room) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/rooms/${room.id}`);
    toast.success(t("deleted"));
    load();
  };

  const applyHabitable = async () => {
    try {
      await api.put(`/properties/${propertyId}`, { habitable_space: habitable });
      toast.success(`${t("habitableArea")}: ${habitable} m² ✓`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const scheduleText = () =>
    `${property?.name || ""} — ${t("roomSchedule")}\n\n` +
    rooms.map((r) =>
      `${r.name}${r.floor ? ` (${r.floor})` : ""}${r.exclude_habitable ? " *" : ""} — ${roomArea(r).toFixed(1)} m²` +
      (r.description ? `\n  ${r.description}` : "")
    ).join("\n") +
    `\n\n${t("habitableArea")}: ${habitable.toFixed(1)} m²`;

  const saveSketch = async (sketch) => {
    const manual = sketchRoom.area_manual;
    const area = manual != null && manual !== "" ? Number(manual) : sketch.area;
    await api.put(`/rooms/${sketchRoom.id}`, { sketch, area });
    toast.success(t("saved"));
    setSketchRoom(null);
    load();
  };

  return (
    <div data-testid="rooms-page">
      <Link to="/properties" data-testid="back-to-properties-link"
        className="no-print inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-900 mb-4 transition-colors">
        <ArrowLeft size={14} strokeWidth={1.5} /> {t("properties")}
      </Link>

      <div className="no-print flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-stone-900">{t("roomMeasurements")}</h1>
          <p className="text-sm text-stone-500 mt-1.5">{property?.name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CopyButton text={scheduleText()} testId="copy-schedule-button" label={t("copySchedule")} />
          <button data-testid="print-schedule-button" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-900 border border-stone-200 hover:border-stone-300 rounded-lg px-3 py-1.5 transition-colors bg-white">
            <Printer size={13} strokeWidth={1.5} /> {t("printSchedule")}
          </button>
          <button data-testid="add-room-button" onClick={() => setEditRoom(null)}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addRoom")}
          </button>
        </div>
      </div>

      <div className="no-print">
        {rooms.length === 0 ? <EmptyState message={t("noResults")} /> : (
          <div className="space-y-3">
            {rooms.map((room, i) => (
              <div key={room.id} data-testid={`room-card-${room.id}`}
                className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 transition-all duration-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button data-testid={`room-up-${room.id}`} onClick={() => reorder(i, -1)} disabled={i === 0}
                      className="text-stone-300 hover:text-stone-700 disabled:opacity-30 p-0.5"><ArrowUp size={14} /></button>
                    <button data-testid={`room-down-${room.id}`} onClick={() => reorder(i, 1)} disabled={i === rooms.length - 1}
                      className="text-stone-300 hover:text-stone-700 disabled:opacity-30 p-0.5"><ArrowDown size={14} /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-heading text-lg font-medium text-stone-900">{room.name}</h3>
                      {room.floor && <span className="bg-stone-100 text-stone-500 rounded-full px-2.5 py-0.5 text-[11px] font-medium">{room.floor}</span>}
                      {room.exclude_habitable && <span className="bg-stone-100 text-stone-400 border border-stone-200 rounded-full px-2.5 py-0.5 text-[11px]">{t("excludeHabitable")}</span>}
                      {room.needs_verification && (
                        <span className="inline-flex items-center gap-1 bg-orange-50 text-[#9A3412] rounded-full px-2.5 py-0.5 text-[11px] font-medium">
                          <Flag size={10} strokeWidth={2} /> {t("needsVerification")}
                        </span>
                      )}
                    </div>
                    {room.description && <p className="text-sm text-stone-500 mt-1 line-clamp-1">{room.description}</p>}
                  </div>
                  <p data-testid={`room-area-${room.id}`} className="font-heading text-2xl font-medium text-stone-900 whitespace-nowrap">
                    {roomArea(room).toFixed(1)} <span className="text-sm text-stone-400 font-body">m²</span>
                  </p>
                  <div className="flex items-center gap-1">
                    <button data-testid={`room-sketch-${room.id}`} onClick={() => setSketchRoom(room)} title={t("sketchRoom")}
                      className="inline-flex items-center gap-1.5 bg-[#2F4F4F] hover:bg-[#243c3c] text-white rounded-lg px-3 py-2 text-xs font-medium transition-colors">
                      <PenTool size={13} strokeWidth={1.5} /> {t("sketchRoom")}
                    </button>
                    <button data-testid={`room-edit-${room.id}`} onClick={() => setEditRoom(room)} title={t("edit")}
                      className="p-2 text-stone-400 hover:text-stone-900 transition-colors"><Pencil size={15} strokeWidth={1.5} /></button>
                    <button data-testid={`room-duplicate-${room.id}`} onClick={() => duplicate(room)} title={t("duplicate")}
                      className="p-2 text-stone-400 hover:text-stone-900 transition-colors"><CopyIcon size={15} strokeWidth={1.5} /></button>
                    <button data-testid={`room-delete-${room.id}`} onClick={() => removeRoom(room)} title={t("delete")}
                      className="p-2 text-stone-400 hover:text-[#7F1D1D] transition-colors"><Trash2 size={15} strokeWidth={1.5} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Room schedule summary (also the printable area) */}
      <div className="print-area bg-white border border-stone-200 rounded-xl p-6 sm:p-8 mt-8">
        <h2 className="font-heading text-2xl font-medium text-stone-900">{property?.name}</h2>
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 mt-1 mb-5">{t("roomSchedule")}</p>
        <div className="divide-y divide-stone-200">
          {rooms.map((room) => (
            <div key={room.id} className="py-3 flex items-baseline justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-800">
                  {room.name}{room.floor ? <span className="text-stone-400 font-normal"> · {room.floor}</span> : null}
                  {room.exclude_habitable ? <span className="text-stone-400 font-normal"> *</span> : null}
                </p>
                {room.description && <p className="text-xs text-stone-500 mt-0.5">{room.description}</p>}
              </div>
              <p className="text-sm font-medium text-stone-900 whitespace-nowrap">{roomArea(room).toFixed(1)} m²</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t-2 border-stone-800 mt-2 pt-4">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-700">{t("habitableArea")}</p>
          <p data-testid="habitable-total" className="font-heading text-3xl font-medium text-stone-900">
            {habitable.toFixed(1)} <span className="text-base text-stone-400 font-body">m²</span>
          </p>
        </div>
        {rooms.some((r) => r.exclude_habitable) && (
          <p className="text-[11px] text-stone-400 mt-2">* {t("excludeHabitable")}</p>
        )}
        <div className="no-print mt-5">
          <button data-testid="apply-habitable-button" onClick={applyHabitable}
            className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-4 py-2 text-xs font-medium transition-colors">
            {t("applyToProperty")} → {habitable.toFixed(1)} m²
          </button>
        </div>
      </div>

      {editRoom !== undefined && (
        <RoomDialog
          room={editRoom}
          propertyId={propertyId}
          existingCount={rooms.length}
          onClose={() => setEditRoom(undefined)}
          onSaved={load}
        />
      )}
      {sketchRoom && (
        <RoomSketch room={sketchRoom} onClose={() => setSketchRoom(null)} onSave={saveSketch} />
      )}
    </div>
  );
}
